import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule
} from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxPaginationModule } from 'ngx-pagination';
import { ExtendedTypeaheadComponent } from './components/extended-typeahead/extended-typeahead.component';
// Core
import { OirHttpServiceInterceptor } from './services/http/oir-http.interceptor';
import { OirErrorHandler } from './services/error/error.handler';
import { ConfigurationService } from './services/configuration/configuration.service';
// Components
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { CollapsibleCardComponent } from './components/collapsible-card/collapsible-card.component';
import { CollapsiblePanelComponent } from './components/collapsible-panel/collapsible-panel.component';
import { CopyToClipboardComponent } from './components/copy-to-clipboard/copy-to-clipboard.component';
import { DatePickerComponent } from './components/datepicker/datepicker.component';
import { InternationalisationComponent } from './components/internationalisation/internationalisation.component';
import { ExpandableInstanceComponent } from './components/expandable-instance/expandable-instance.component';
import { InstanceComponent } from './components/instance/instance.component';
import { ModalComponent } from './components/modal/modal.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { NotificationsTrayComponent } from './components/notifications/notifications-tray/notifications-tray.component';
import { MultiselectComponent } from './components/select/multiselect.component';
import { SelectHeadingPipe } from './components/select/select.pipes';
import { SingleselectComponent } from './components/select/singleselect.component';
import { OverlayComponent } from './components/overlay/overlay.component';
import { ToastNotificationsComponent } from './components/notifications/toast-notifications/toast-notifications.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { UnderlineDirective } from './components/typeahead/typeahead-underline.directive';
import { TypeaheadComponent } from './components/typeahead/typeahead.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { ConcurrentAccessComponent } from './components/concurrent-access/concurrent-access.component';
import { ModuleInfoComponent } from './components/module-info/module-info.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { SelectableListComponent } from './components/selectable-list/selectable-list.component';
import { ToggleComponent } from './components/toggle/toggle.component';
import { OirDialogComponent } from './components/oir-dialog/oir-dialog.component';
import { VideoSuggestionsComponent } from './components/video-suggestions/video-suggestions.component';
import { CurrentRouteVideosPipe } from './components/video-suggestions/current-route-videos.pipe';

// Directives
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { ElipsisDirective } from './directives/elipsis.directive';
import { InfiniteScrollDirective } from './directives/infinite-scroll.directive';
import { ResizeDirective } from './directives/resize.directive';
import { TabbableDirective } from './directives/tabbable.directive';
import { ValidateDirective } from './directives/validate.directive';
// Pipes
import { MomentPipe } from './pipes/moment.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { HtmlToTextPipe } from './pipes/html-text.pipe';
import { TabIndexPipe } from './pipes/tabindex.pipe';
// Guards
import { ActionBarComponent } from './components/action-bar/action-bar.component';
import { ActionButtonComponent } from './components/action-button/action-button.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { OptionAccordionComponent } from './components/option-accordion/option-accordion.component';
import { ScrollTagsComponent } from './components/tag-scroll/tag-scroll.component';
import { ScrollTagDirective } from './components/tag-scroll/tag-scroll.directive';

const COMPONENTS = [
  AccessDeniedComponent,
  NavigationComponent,
  NotificationsTrayComponent,
  ToastNotificationsComponent,
  InternationalisationComponent,
  ModalComponent,
  OverlayComponent,
  MomentPipe,
  TabIndexPipe,
  SafeHtmlPipe,
  SafeUrlPipe,
  HtmlToTextPipe,
  ResizeDirective,
  ElipsisDirective,
  ClickOutsideDirective,
  InfiniteScrollDirective,
  ValidateDirective,
  TabbableDirective,
  SelectHeadingPipe,
  MultiselectComponent,
  SingleselectComponent,
  CollapsibleCardComponent,
  CollapsiblePanelComponent,
  CopyToClipboardComponent,
  AccordionComponent,
  DatePickerComponent,
  NotFoundComponent,
  SpinnerComponent,
  TypeaheadComponent,
  UnderlineDirective,
  ConcurrentAccessComponent,
  ModuleInfoComponent,
  PaginationComponent,
  ToggleComponent,
  SelectableListComponent,
  InstanceComponent,
  ExpandableInstanceComponent,
  OptionAccordionComponent,
  ActionBarComponent,
  ActionButtonComponent,
  FileUploadComponent,
  ScrollTagsComponent,
  ScrollTagDirective,
  VideoSuggestionsComponent,
  CurrentRouteVideosPipe,
  OirDialogComponent,
  ExtendedTypeaheadComponent
];

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    TranslateModule,
    MatDialogModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    NgxPaginationModule,
    FileUploadModule,
    OverlayModule
  ],
  declarations: [...COMPONENTS, ExtendedTypeaheadComponent],
  exports: [...COMPONENTS],
  providers: [
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false } },
    { provide: ErrorHandler, useClass: OirErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OirHttpServiceInterceptor,
      multi: true
    }
  ]
})
export class ClientFrameworkModule {}
